<template>
  <div class="downtimes-page">
    <div class="toolbar">
      <h1 class="title">Статистика: простои</h1>
      <button class="btn" type="button" :disabled="isRefreshing" @click="refreshAll">
        {{ isRefreshing ? 'Обновление…' : 'Обновить' }}
      </button>
    </div>

    <div v-if="forbidden" class="state-block">
      Нет доступа: требуется право «Статистика: общая» (statistics.general).
    </div>
    <div v-else-if="snapshotError" class="state-block">
      Снимок простоев ещё строится, повторите попытку через минуту.
    </div>
    <div v-else-if="hasError" class="state-block">
      Не удалось загрузить данные.
      <button class="btn" type="button" @click="refreshAll">Повторить</button>
    </div>

    <template v-else>
      <div class="filters-panel">
        <DatePresetsDropdown
          :date-from="filters.dateFrom"
          :date-to="filters.dateTo"
          :initial-preset="'d30'"
          @change="onPeriodChange"
        />
        <MultiDropdown
          label="Логист"
          :options="logistOptions"
          :model-value="filters.responsibles"
          @update:model-value="filters.responsibles = $event"
        />
        <MultiDropdown
          label="Операция"
          :options="OPERATION_OPTIONS"
          :model-value="filters.operationTypes"
          @update:model-value="filters.operationTypes = $event"
        />
        <MultiDropdown
          label="Классификация"
          :options="CLASSIFICATION_OPTIONS"
          :model-value="classificationsModel"
          :clear-emits-all="true"
          @update:model-value="onClassificationsChange"
        />
        <MultiDropdown
          label="Этап"
          :options="LIFECYCLE_OPTIONS"
          :model-value="filters.lifecycles"
          @update:model-value="filters.lifecycles = $event"
        />
        <FilterPresetsDropdown
          storage-key="statisticsDowntimesFilterPresets"
          :current="downtimesSnapshot"
          @apply="applyDowntimesSnapshot"
        />
        <button class="btn btn-reset" type="button" @click="onResetFilters">Сбросить</button>
      </div>

      <my-loader v-if="kpisQuery.isLoading.value && !kpis" />

      <template v-else>
        <div v-if="kpis" class="kpi-row" :class="{ 'is-stale': isRefetching }">
          <div class="kpi-card">
            <div class="kpi-label">Точек маршрута</div>
            <div class="kpi-value">{{ fmtNum(kpis.points_total) }}</div>
            <div class="kpi-sub">рейсов: {{ fmtNum(kpis.trips_total) }}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">Подтверждённые</div>
            <div class="kpi-value">{{ fmtNum(kpis.confirmed) }}</div>
            <div class="kpi-sub">потенциальных: {{ fmtNum(kpis.potential) }}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">Зарегистрировано</div>
            <div class="kpi-value">{{ fmtNum(kpis.registered) }}</div>
            <div class="kpi-sub">активных: {{ fmtNum(kpis.active) }}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">Ср. просрочка</div>
            <div class="kpi-value">{{ fmtNum(kpis.avg_overdue, 1) }} ч</div>
            <div class="kpi-sub">медиана: {{ fmtNum(kpis.median_overdue, 1) }} ч</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">Простои</div>
            <div class="kpi-value">{{ fmtHours(kpis.total_downtime_hours * 3600) }}</div>
            <div class="kpi-sub">{{ fmtMoney(kpis.total_downtime_cost) }} по 700 ₽/ч</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">Ср. задержка прибытия</div>
            <div class="kpi-value">{{ fmtHours(kpis.avg_delay_min * 60) }}</div>
            <div class="kpi-sub">ручная регистрация: {{ fmtNum(kpis.manual_reg) }}</div>
          </div>
        </div>

        <div class="tabs">
          <button
            v-for="tab in TABS"
            :key="tab.id"
            class="tab-btn"
            type="button"
            :class="{ active: activeTab === tab.id }"
            @click="activeTab = tab.id"
          >
            {{ tab.name }}
          </button>
        </div>

        <div class="tab-content" :class="{ 'is-stale': isRefetching }">
          <div v-show="activeTab === 'overview'" class="overview-tab">
            <div class="chart-block">
              <div class="chart-head">
                <div class="chart-title">Точки по дням (по статусам)</div>
                <div class="chart-hint">Клик по столбцу — фильтр таблицы по дате</div>
              </div>
              <VChart
                v-if="dayRows.length"
                class="chart"
                :option="dayOption"
                autoresize
                @click="onDayChartClick"
                @legendselectchanged="onStatusLegendChange"
              />
              <div v-else class="chart-empty">Нет данных за период</div>
            </div>

            <div class="charts-row charts-row-pies">
              <StatPieChart
                title="Итого по статусам"
                :rows="statusTotals"
                :height="360"
                :value-formatter="(v) => fmtNum(v)"
                empty-text="Нет данных за период"
              />
              <StatPieChart
                title="Точные статусы"
                :rows="reasonRows"
                :height="360"
                :legend-width="280"
                :value-formatter="(v) => fmtNum(v)"
                empty-text="Нет данных за период"
              />
            </div>

            <div class="table-head">
              <div class="table-title">
                Точки
                <span v-if="selectedDay" class="day-chip">
                  за {{ fmtDate(selectedDay) }}
                  <button class="chip-x" type="button" title="Сбросить дату" @click="clearSelectedDay">✕</button>
                </span>
              </div>
            </div>
            <div class="table-scroll">
              <table class="stat-table" v-if="tableRows.length">
                <thead>
                  <tr>
                    <th
                      v-for="col in TABLE_COLUMNS"
                      :key="col.id"
                      :class="{ num: col.num, sortable: col.sortable }"
                      @click="onSort(col)"
                    >
                      {{ col.header }}
                      <span v-if="sortMark(col.id)">{{ sortMark(col.id) }}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in tableRows" :key="row.route_point_id" :class="rowClass(row)">
                    <td class="num">
                      <button v-if="row.trip_id && canOpenTrip360" type="button" class="trip-link" title="Открыть 360° окно рейса" @click="openTrip360(row.trip_id)">{{ row.trip_id }}</button>
                      <template v-else>—</template>
                    </td>
                    <td>{{ row.responsible_name || '—' }}</td>
                    <td>{{ fmtDate(row.planned_date) }}</td>
                    <td>{{ row.warehouse_name || '—' }}</td>
                    <td>{{ opLabel(row.operation_type) }}</td>
                    <td class="num">{{ row.delay_minutes == null ? '—' : fmtHours(row.delay_minutes * 60) }}</td>
                    <td class="num" :class="{ 'warn-cell': Number(row.overdue_hours) > 0 }">
                      {{ row.overdue_hours == null ? '—' : fmtNum(row.overdue_hours, 1) + ' ч' }}
                    </td>
                    <td>
                      <span class="tag" :style="clsStyle(row.classification)">{{ clsShortLabel(row.classification) }}</span>
                    </td>
                    <td>
                      <span class="tag lifecycle" :class="lifecycleClass(row.lifecycle)">{{ lifecycleLabel(row.lifecycle) }}</span>
                    </td>
                    <td class="reason-cell" :title="row.reason">{{ row.reason || '—' }}</td>
                  </tr>
                </tbody>
              </table>
              <div v-else-if="!tableQuery.isFetching.value" class="empty-table">Нет данных по выбранным фильтрам</div>
            </div>
            <div class="table-footer">
              <span class="total-info">{{ fmtNum(tableTotal) }} точек</span>
              <my-pagination
                :total-pages="totalPages"
                :current-page="tablePage"
                @page-changed="tablePage = $event"
              />
            </div>
          </div>

          <div v-show="activeTab === 'logists'" class="logists-tab">
            <div class="charts-row">
              <StatStackedBarChart
                title="Точки по логистам (по статусам)"
                :rows="logistStackRows"
                :height="logistStackHeight"
                :value-formatter="(v) => fmtNum(v)"
                :legend-selected="statusLegendMap"
                empty-text="Нет данных по выбранным фильтрам"
                @legendselectchanged="onStatusLegendChange"
              />
              <StatNestedPieChart
                title="Итого по статусам"
                :rows="logistStackRows"
                :height="logistStackHeight"
                :value-formatter="(v) => fmtNum(v)"
                :legend-selected="statusLegendMap"
                empty-text="Нет данных по выбранным фильтрам"
                @legendselectchanged="onStatusLegendChange"
                @select-object="onLogistObjectSelect"
              />
            </div>
            <div class="table-scroll">
              <table class="stat-table" v-if="logistRows.length">
                <thead>
                  <tr>
                    <th>Логист</th>
                    <th class="num">Точек</th>
                    <th class="num">Рейсов</th>
                    <th class="num">Подтв.</th>
                    <th class="num">Зарег.</th>
                    <th class="num">Вручную</th>
                    <th class="num">Ср. просрочка</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in logistRows" :key="row.logist">
                    <td class="name-cell">{{ row.logist || '—' }}</td>
                    <td class="num strong">{{ fmtNum(row.points) }}</td>
                    <td class="num">{{ fmtNum(row.trips) }}</td>
                    <td class="num" :class="{ 'warn-cell': row.confirmed > 0 }">{{ fmtNum(row.confirmed) }}</td>
                    <td class="num">{{ fmtNum(row.registered) }}</td>
                    <td class="num">{{ fmtNum(row.manual_reg) }}</td>
                    <td class="num">{{ row.avg_overdue == null ? '—' : fmtNum(row.avg_overdue, 1) + ' ч' }}</td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="empty-table">Нет данных по выбранным фильтрам</div>
            </div>
          </div>

          <div v-show="activeTab === 'warehouses'" class="warehouses-tab">
            <div class="charts-row">
              <StatStackedBarChart
                :title="`Топ ${warehouseLimit} складов: точки (по статусам)`"
                :rows="warehouseStackRows"
                :height="warehouseStackHeight"
                :value-formatter="(v) => fmtNum(v)"
                :legend-selected="statusLegendMap"
                empty-text="Нет данных за период"
                @legendselectchanged="onStatusLegendChange"
              >
                <template #controls>
                  <label class="topn">
                    Топ
                    <select class="topn-select" :value="warehouseLimit" @change="onWarehouseLimitChange">
                      <option v-for="n in WAREHOUSE_LIMITS" :key="n" :value="n">{{ n }}</option>
                    </select>
                  </label>
                </template>
              </StatStackedBarChart>
              <StatNestedPieChart
                title="Итого по статусам"
                :rows="warehouseStackRows"
                :height="warehouseStackHeight"
                :value-formatter="(v) => fmtNum(v)"
                :legend-selected="statusLegendMap"
                empty-text="Нет данных за период"
                @legendselectchanged="onStatusLegendChange"
              />
            </div>
            <div class="table-scroll">
              <table class="stat-table" v-if="warehouseRows.length">
                <thead>
                  <tr>
                    <th>Склад</th>
                    <th class="num">Точек</th>
                    <th class="num">Подтв.</th>
                    <th class="num">Пот.</th>
                    <th class="num">Опозд.</th>
                    <th class="num">Урег.</th>
                    <th class="num">Нет ФД</th>
                    <th class="num">Без кл.</th>
                    <th class="num">Нет плана</th>
                    <th class="num">Ср. просрочка</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in warehouseRows" :key="row.warehouse_name">
                    <td class="name-cell">{{ row.warehouse_name || '—' }}</td>
                    <td class="num strong">{{ fmtNum(row.points) }}</td>
                    <td class="num" :class="{ 'warn-cell': row.confirmed > 0 }">{{ fmtNum(row.confirmed) }}</td>
                    <td class="num">{{ fmtNum(row.potential) }}</td>
                    <td class="num">{{ fmtNum(row.late) }}</td>
                    <td class="num">{{ fmtNum(row.resolved_class) }}</td>
                    <td class="num">{{ fmtNum(row.incomplete) }}</td>
                    <td class="num">{{ fmtNum(row.none_count) }}</td>
                    <td class="num">{{ fmtNum(row.no_plan) }}</td>
                    <td class="num">{{ row.avg_overdue == null ? '—' : fmtNum(row.avg_overdue, 1) + ' ч' }}</td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="empty-table">Нет данных за период</div>
            </div>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useTrip360Store } from '@/stores/trip360';
import MultiDropdown from './components/MultiDropdown.vue';
import DatePresetsDropdown from './components/DatePresetsDropdown.vue';
import FilterPresetsDropdown from './components/FilterPresetsDropdown.vue';
import StatPieChart from './components/StatPieChart.vue';
import StatNestedPieChart from './components/StatNestedPieChart.vue';
import StatStackedBarChart from './components/StatStackedBarChart.vue';
import VChart from '@/components/charts/echarts';
import { DOWNTIMES_PAGE_SIZE, useDowntimesStatistics } from './useDowntimesStatistics';
import { fmtDate, fmtHours, fmtMoney, fmtNum } from './format';
import {
  DOWNTIME_STATUSES,
  DOWNTIME_STATUS_VALUES,
  DOWNTIME_LIFECYCLE_OPTIONS,
  downtimeStatusMeta,
  downtimeLifecycleLabel,
} from './downtimeStatuses.mjs';

const TABS = [
  { id: 'overview', name: 'Обзор' },
  { id: 'logists', name: 'Логисты' },
  { id: 'warehouses', name: 'Склады и статусы' },
];

const OPERATION_OPTIONS = [
  { value: 'loading', label: 'Погрузка' },
  { value: 'unloading', label: 'Выгрузка' },
];
const CLASSIFICATION_OPTIONS = DOWNTIME_STATUSES.map(({ value, label, chartColor }) => ({
  value,
  label,
  color: chartColor,
}));
const LIFECYCLE_OPTIONS = DOWNTIME_LIFECYCLE_OPTIONS;
const WAREHOUSE_LIMITS = [5, 10, 15, 20, 30, 50];

// Колонки API → ключи статусов
const STATUS_COLUMNS = {
  confirmed: 'confirmed',
  potential: 'potential',
  late: 'late',
  resolved: 'resolved_class',
  incomplete: 'incomplete',
  none: 'none_count',
  no_plan: 'no_plan',
};

const TABLE_COLUMNS = [
  { id: 'trip_id', header: 'Рейс', num: true, sortable: true },
  { id: 'responsible_name', header: 'Логист', sortable: true },
  { id: 'planned_date', header: 'План', sortable: true },
  { id: 'warehouse_name', header: 'Склад', sortable: true },
  { id: 'operation_type', header: 'Операция' },
  { id: 'delay_minutes', header: 'Задержка', sortable: true },
  { id: 'overdue_hours', header: 'Просрочка', num: true, sortable: true },
  { id: 'classification', header: 'Класс', sortable: true },
  { id: 'lifecycle', header: 'Этап', sortable: true },
  { id: 'reason', header: 'Статус' },
];

export default {
  name: 'DowntimesStatisticsPage',
  components: { MultiDropdown, DatePresetsDropdown, FilterPresetsDropdown, StatPieChart, StatNestedPieChart, StatStackedBarChart, VChart },
  setup() {
    const store = useStore();
    const trip360 = useTrip360Store();
    const {
      filters,
      statusSelection,
      selectedDay,
      warehouseLimit,
      applySnapshot,
      kpisQuery,
      byDayQuery,
      byLogistQuery,
      byWarehouseQuery,
      byReasonQuery,
      logistOptionsQuery,
      tableQuery,
      tableSorting,
      tablePage,
      refreshAll,
    } = useDowntimesStatistics();

    const activeTab = ref('overview');
    const canOpenTrip360 = computed(() => store.getters.hasPermission('trip360.view'));

    const forbidden = computed(() => {
      const error = kpisQuery.error.value;
      return Boolean(error && error.response && error.response.status === 403);
    });
    const snapshotError = computed(() => {
      const error = kpisQuery.error.value;
      return Boolean(error && error.response && error.response.status === 503);
    });
    const hasError = computed(() => Boolean(kpisQuery.error.value));

    const kpis = computed(() => kpisQuery.data.value || null);
    const isRefreshing = computed(() => kpisQuery.isFetching.value);
    // Рефетч при смене фильтров: старые данные остаются на экране (placeholderData),
    // контент лишь слегка затухает — без размонтирования и мигания.
    const isRefetching = computed(() => kpisQuery.isFetching.value && Boolean(kpis.value));

    const onPeriodChange = ({ dateFrom, dateTo }) => {
      filters.dateFrom = dateFrom;
      filters.dateTo = dateTo;
      selectedDay.value = null;
    };
    const onResetFilters = () => {
      filters.responsibles = [];
      filters.lifecycles = [];
      filters.operationTypes = [];
      statusSelection.value = null;
    };

    // Пресеты фильтров: снапшот текущего состояния + применение.
    const applyDowntimesSnapshot = applySnapshot;
    const downtimesSnapshot = computed(() => ({
      ...JSON.parse(JSON.stringify(filters)),
      classifications: statusSelection.value || [],
    }));

    // ---------------------------------------------------------------
    // Классификации: statusSelection (null=все / массив=явная выборка) —
    // единственный источник истины; дропдаун и легенда пишут только в него.
    // ---------------------------------------------------------------
    const classificationsModel = computed(() => statusSelection.value ?? DOWNTIME_STATUS_VALUES);
    const onClassificationsChange = (next) => {
      statusSelection.value = next.length === DOWNTIME_STATUS_VALUES.length ? null : next;
    };

    // ---------------------------------------------------------------
    // График по дням: stacked по статусам + shared-состояние с фильтром
    // классификаций (легенда ⇄ фильтр).
    // ---------------------------------------------------------------
    // echarts мерджит legend.selected с внутренним состоянием, поэтому
    // карта ВСЕГДА явная и полная (null = все true) — иначе снятые статусы
    // «залипают» в легенде после сброса фильтра.
    const statusLegendMap = computed(() => {
      const selection = statusSelection.value;
      const map = {};
      for (const status of DOWNTIME_STATUSES) {
        map[status.label] = selection === null ? true : selection.includes(status.value);
      }
      return map;
    });

    const dayRows = computed(() => byDayQuery.data.value || []);
    const dayLabels = computed(() => {
      const map = new Map();
      for (const row of dayRows.value) {
        const label = fmtDate(row.day);
        if (!map.has(label)) map.set(label, row.day);
      }
      return map;
    });
    const dayOption = computed(() => {
      const rows = dayRows.value;
      const days = [...dayLabels.value.keys()];
      const counts = new Map();
      for (const row of rows) {
        const cls = row.classification || 'none';
        const key = `${row.day}|${cls}`;
        counts.set(key, (counts.get(key) || 0) + (Number(row.points) || 0));
      }
      const selected = statusLegendMap.value;
      return {
        grid: { left: 8, right: 24, top: 32, bottom: 24, containLabel: true },
        legend: { top: 0, textStyle: { fontSize: 11, color: '#667085' }, selected },
        tooltip: { trigger: 'axis', confine: true },
        xAxis: {
          type: 'category',
          data: days,
          axisLabel: { rotate: 45, fontSize: 10 },
        },
        yAxis: { type: 'value' },
        series: DOWNTIME_STATUSES.map(({ value, label, chartColor }) => ({
          name: label,
          type: 'bar',
          stack: 'total',
          barMaxWidth: 28,
          itemStyle: { color: chartColor },
          cursor: 'pointer',
          data: days.map((dayLabel) => {
            const dayKey = dayLabels.value.get(dayLabel);
            return counts.get(`${dayKey}|${value}`) || 0;
          }),
        })),
      };
    });

    const onDayChartClick = (params) => {
      const dayKey = dayLabels.value.get(params.name);
      if (dayKey === undefined) return;
      selectedDay.value = selectedDay.value === dayKey ? null : dayKey;
      tablePage.value = 1;
    };
    // Общий обработчик кликов по легенде для ВСЕХ stacked-графиков
    // (дни/логисты/склады) — одно общее состояние статусного фильтра.
    const onStatusLegendChange = (event) => {
      const next = DOWNTIME_STATUS_VALUES.filter(
        (value) => event.selected[downtimeStatusMeta(value).label] !== false
      );
      statusSelection.value = next.length === DOWNTIME_STATUS_VALUES.length ? null : next;
    };
    const clearSelectedDay = () => {
      selectedDay.value = null;
      tablePage.value = 1;
    };

    // ---------------------------------------------------------------
    // Логисты / склады: stacked по статусам; статусы: полный список.
    // ---------------------------------------------------------------
    const toStackRow = (row, label) => {
      const values = {};
      for (const [status, column] of Object.entries(STATUS_COLUMNS)) {
        values[status] = Number(row[column]) || 0;
      }
      return { label, values };
    };

    const logistRows = computed(() => byLogistQuery.data.value || []);
    const logistStackRows = computed(() =>
      logistRows.value.filter((row) => row.logist).map((row) => toStackRow(row, row.logist))
    );
    const logistStackHeight = computed(() =>
      Math.min(560, logistStackRows.value.length * 30 + 80)
    );
    // Клик по логисту (внутренний круг pie) — точечный фильтр по логисту.
    const onLogistObjectSelect = (name) => {
      filters.responsibles =
        filters.responsibles.length === 1 && filters.responsibles[0] === name ? [] : [name];
    };
    const logistOptions = computed(() =>
      (logistOptionsQuery.data.value || [])
        .filter((row) => row.logist)
        .map((row) => ({ value: row.logist, label: row.logist }))
    );

    const warehouseRows = computed(() => byWarehouseQuery.data.value || []);
    const warehouseStackRows = computed(() =>
      warehouseRows.value.map((row) => toStackRow(row, row.warehouse_name || '—'))
    );
    const warehouseStackHeight = computed(() =>
      Math.min(560, warehouseStackRows.value.length * 30 + 80)
    );
    const onWarehouseLimitChange = (event) => {
      warehouseLimit.value = Number(event.target.value) || 15;
    };

    // Итоги по статусам для обзора — из by-logist (учитывает ВСЕ фильтры,
    // включая статусные и выбранного логиста; NULL-логист входит в сумму).
    const statusTotals = computed(() => {
      const totals = {};
      for (const status of DOWNTIME_STATUSES) totals[status.value] = 0;
      for (const row of logistRows.value) {
        for (const [status, column] of Object.entries(STATUS_COLUMNS)) {
          totals[status] += Number(row[column]) || 0;
        }
      }
      return DOWNTIME_STATUSES.map(({ value, label, chartColor }) => ({
        label,
        color: chartColor,
        value: totals[value],
      })).filter((row) => row.value > 0);
    });

    const reasonRows = computed(() =>
      (byReasonQuery.data.value || []).map((row) => ({
        label: row.reason || '—',
        value: Number(row.points) || 0,
      }))
    );

    // ---------------------------------------------------------------
    // Таблица
    // ---------------------------------------------------------------
    const tableRows = computed(() => (tableQuery.data.value && tableQuery.data.value.rows) || []);
    const tableTotal = computed(() => (tableQuery.data.value && tableQuery.data.value.total) || 0);
    const totalPages = computed(() => Math.max(1, Math.ceil(tableTotal.value / DOWNTIMES_PAGE_SIZE)));

    const onSort = (col) => {
      if (!col.sortable) return;
      const current = tableSorting.value[0];
      if (current && current.id === col.id) {
        tableSorting.value = [{ id: col.id, desc: !current.desc }];
      } else {
        tableSorting.value = [{ id: col.id, desc: true }];
      }
      tablePage.value = 1;
    };
    const sortMark = (id) => {
      const entry = tableSorting.value.find((item) => item.id === id);
      if (!entry) return '';
      return entry.desc ? '↓' : '↑';
    };

    const opLabel = (type) => (type === 'loading' ? 'Погрузка' : type === 'unloading' ? 'Выгрузка' : type || '—');
    const openTrip360 = (tripId) => {
      if (!tripId) return;
      trip360.openTrip360(String(tripId), { tab: 'downtime' });
    };
    const clsStyle = (cls) => {
      const meta = downtimeStatusMeta(cls);
      return { color: meta.color, backgroundColor: meta.background };
    };
    const clsShortLabel = (cls) => downtimeStatusMeta(cls).shortLabel;
    const lifecycleLabel = (value) => downtimeLifecycleLabel(value);
    const lifecycleClass = (value) => `lc-${value || 'none'}`;
    const rowClass = (row) => `row-cls-${row.classification || 'none'}`;

    watch(
      () => JSON.stringify([statusSelection.value, filters.lifecycles, filters.operationTypes, filters.responsibles]),
      () => {
        tablePage.value = 1;
      }
    );

    return {
      TABS,
      OPERATION_OPTIONS,
      CLASSIFICATION_OPTIONS,
      LIFECYCLE_OPTIONS,
      WAREHOUSE_LIMITS,
      TABLE_COLUMNS,
      DOWNTIMES_PAGE_SIZE,
      filters,
      selectedDay,
      warehouseLimit,
      activeTab,
      canOpenTrip360,
      forbidden,
      snapshotError,
      hasError,
      kpis,
      isRefetching,
      kpisQuery,
      isRefreshing,
      classificationsModel,
      onClassificationsChange,
      onPeriodChange,
      onResetFilters,
      applyDowntimesSnapshot,
      downtimesSnapshot,
      dayRows,
      dayOption,
      statusLegendMap,
      statusTotals,
      onDayChartClick,
      onStatusLegendChange,
      clearSelectedDay,
      logistRows,
      logistStackRows,
      logistStackHeight,
      onLogistObjectSelect,
      logistOptions,
      warehouseRows,
      warehouseStackRows,
      warehouseStackHeight,
      onWarehouseLimitChange,
      reasonRows,
      tableQuery,
      tableRows,
      tableTotal,
      totalPages,
      tablePage,
      tableSorting,
      onSort,
      sortMark,
      opLabel,
      openTrip360,
      clsStyle,
      clsShortLabel,
      lifecycleLabel,
      lifecycleClass,
      rowClass,
      refreshAll,
      fmtDate,
      fmtHours,
      fmtMoney,
      fmtNum,
    };
  },
};
</script>

<style scoped>
.downtimes-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
  padding: 20px 24px 40px;
  box-sizing: border-box;
  overflow-y: auto;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 16px;
}

.title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #111827;
}

.btn {
  padding: 6px 14px;
  border-radius: 6px;
  border: 1px solid #d0d5dd;
  background: #fff;
  font-size: 13px;
  color: #344054;
  cursor: pointer;
}

.btn:hover {
  border-color: #98a2b3;
}

.btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.state-block {
  padding: 40px;
  text-align: center;
  color: #667085;
  font-size: 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
}

.filters-panel {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
}

.btn-reset {
  margin-left: auto;
}

.kpi-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.kpi-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 16px;
  background: #fff;
}

.kpi-label {
  font-size: 12px;
  color: #667085;
  margin-bottom: 6px;
}

.kpi-value {
  font-size: 22px;
  font-weight: 700;
  color: #1d2939;
  font-variant-numeric: tabular-nums;
}

.kpi-sub {
  margin-top: 4px;
  font-size: 12px;
  color: #98a2b3;
}

.tabs {
  display: flex;
  gap: 4px;
  border-bottom: 1px solid #e5e7eb;
}

.tab-btn {
  padding: 8px 16px;
  border: none;
  background: none;
  font-size: 14px;
  color: #667085;
  cursor: pointer;
  border-bottom: 2px solid transparent;
}

.tab-btn.active {
  color: #007bff;
  border-bottom-color: #007bff;
  font-weight: 600;
}

.tab-content {
  min-height: 200px;
}

.kpi-row,
.tab-content {
  transition: opacity 0.15s ease;
}

.is-stale {
  opacity: 0.55;
}

.overview-tab,
.logists-tab,
.warehouses-tab {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

.chart-block {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  padding: 14px;
}

.chart-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.chart-title {
  font-size: 14px;
  font-weight: 600;
  color: #344054;
}

.chart-hint {
  font-size: 12px;
  color: #98a2b3;
}

.chart {
  width: 100%;
  height: 360px;
}

.chart-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: #98a2b3;
  font-size: 14px;
}

.table-head {
  display: flex;
  align-items: center;
}

.table-title {
  font-size: 14px;
  font-weight: 600;
  color: #344054;
  display: flex;
  align-items: center;
  gap: 8px;
}

.day-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px;
  border-radius: 10px;
  background: #eef4ff;
  color: #0056b3;
  font-size: 12px;
  font-weight: 600;
}

.chip-x {
  padding: 0;
  border: none;
  background: none;
  color: #0056b3;
  font-size: 12px;
  cursor: pointer;
  line-height: 1;
}

.chip-x:hover {
  color: #b42318;
}

.warehouses-tab {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

/* Stacked-бар + итоговый pie рядом */
.charts-row {
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr);
  gap: 16px;
  align-items: stretch;
}

/* Два pie в обзоре: итого : точные = 1 : 2 */
.charts-row-pies {
  grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
}

@media (max-width: 1100px) {
  .charts-row,
  .charts-row-pies {
    grid-template-columns: 1fr;
  }
}

.topn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #667085;
  white-space: nowrap;
}

.topn-select {
  padding: 3px 6px;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  background: #fff;
  font: inherit;
  font-size: 12px;
  color: #344054;
  cursor: pointer;
}

.table-scroll {
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
}

.stat-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.stat-table th {
  position: sticky;
  top: 0;
  background: #f8fafc;
  color: #667085;
  font-weight: 600;
  text-align: left;
  padding: 10px 12px;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
}

.stat-table th.sortable {
  cursor: pointer;
  user-select: none;
}

.stat-table th.sortable:hover {
  color: #344054;
}

.stat-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #f2f4f7;
  color: #1d2939;
  white-space: nowrap;
}

.stat-table tbody tr:hover {
  background: #f0f6ff;
}

/* Подсветка строк по классификации — палитра страницы простоев */
.stat-table tbody tr.row-cls-confirmed { background: #fee4e2; }
.stat-table tbody tr.row-cls-potential { background: #fef0c7; }
.stat-table tbody tr.row-cls-late { background: #ffead5; }
.stat-table tbody tr.row-cls-resolved { background: #ecfdf3; }
.stat-table tbody tr.row-cls-incomplete { background: #eff8ff; }
.stat-table tbody tr.row-cls-no_plan { background: #f2f4f7; }

.stat-table tbody tr.row-cls-confirmed:hover { background: #fddbd7; }
.stat-table tbody tr.row-cls-potential:hover { background: #fde6ad; }
.stat-table tbody tr.row-cls-late:hover { background: #ffdcc0; }
.stat-table tbody tr.row-cls-resolved:hover { background: #d3f8e3; }
.stat-table tbody tr.row-cls-incomplete:hover { background: #ddeefe; }
.stat-table tbody tr.row-cls-no_plan:hover { background: #e6e8eb; }

.tag {
  display: inline-block;
  padding: 2px 7px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
}

.tag.lifecycle {
  background: #f2f4f7;
  color: #475467;
}

.tag.lifecycle.lc-registered {
  background: #eef4ff;
  color: #175cd3;
}

.tag.lifecycle.lc-resolved {
  background: #ecfdf3;
  color: #027a48;
}

.tag.lifecycle.lc-stale {
  background: #fcfaee;
  color: #b54708;
}

.reason-cell {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.trip-link {
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  color: #175cd3;
  cursor: pointer;
  text-decoration: underline;
}

.trip-link:hover {
  color: #007bff;
}

.strong {
  font-weight: 600;
}

.name-cell {
  font-weight: 500;
}

.warn-cell {
  color: #d92d20;
}

.table-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.total-info {
  font-size: 13px;
  color: #667085;
}

.empty-table {
  padding: 40px;
  text-align: center;
  color: #98a2b3;
  font-size: 14px;
}
</style>
